import { Doc } from "@/components/Doc/Doc";
import { FlashOnChangeTD } from "@/components/FlashOnChange/FlashOnChange";
import { LabelExplainer } from "@/components/LabelExplainer/LabelExplainer";
import { Panel } from "@/components/Panel/Panel";
import {
  acquisitionInvestorsDollarCommonEquityAtom,
  acquisitionInvestorsDollarPreferredEquityAtom,
  promoteSearchersCarryDollarCommonEquityAtom,
  searcherCoInvestDollarCommonEquityAtom,
  searcherCoInvestDollarPreferredEquityAtom,
  searchPhaseInvestorsDollarCommonEquityAtom,
  searchPhaseInvestorsDollarPreferredEquityAtom,
  sellerRolloverDollarCommonEquityAtom,
  sellerRolloverDollarPreferredEquityAtom,
  totalCommonEquityDollarValueAtom,
  totalPreferredEquityDollarValueAtom,
} from "@/financeModels/owasco/companyOwnership";
import {
  allInvestorTypesAtom,
  InvestorType,
} from "@/financeModels/owasco/returnMetrics";
import { monospaceFontFamily } from "@/theme";
import { formatMoney } from "@/utils/moneyFormating";
import { Table } from "@mantine/core";
import { useAtomValue } from "jotai";

export const CapTableEquityValue = () => {
  const investorValues = useAtomValue(allInvestorTypesAtom);

  function isVisible(id: InvestorType["id"]) {
    return (
      investorValues
        .filter((row) => row.isVisible)
        .filter((row) => row.id === id).length > 0
    );
  }

  const values = [
    {
      preferred: useAtomValue(searchPhaseInvestorsDollarPreferredEquityAtom),
      common: useAtomValue(searchPhaseInvestorsDollarCommonEquityAtom),
      isVisible: isVisible("searchPhase"),
    },
    {
      preferred: useAtomValue(acquisitionInvestorsDollarPreferredEquityAtom),
      common: useAtomValue(acquisitionInvestorsDollarCommonEquityAtom),
      isVisible: isVisible("acquisition"),
    },
    {
      preferred: useAtomValue(searcherCoInvestDollarPreferredEquityAtom),
      common: useAtomValue(searcherCoInvestDollarCommonEquityAtom),
      isVisible: true,
    },
    {
      preferred: useAtomValue(sellerRolloverDollarPreferredEquityAtom),
      common: useAtomValue(sellerRolloverDollarCommonEquityAtom),
      isVisible: isVisible("rollover"),
    },
    {
      preferred: null,
      common: useAtomValue(promoteSearchersCarryDollarCommonEquityAtom),
      isVisible: isVisible("promote"),
    },
    {
      preferred: useAtomValue(totalPreferredEquityDollarValueAtom),
      common: useAtomValue(totalCommonEquityDollarValueAtom),
      isVisible: true,
      highlight: true,
    },
  ];

  return (
    <Panel p={0} w={310}>
      <Table striped withColumnBorders withTableBorder>
        <Table.Thead>
          <Table.Tr h={58}>
            <Table.Th>
              <LabelExplainer
                label={
                  <span style={{ whiteSpace: "nowrap", lineHeight: 1.6 }}>
                    Post Step-Up:
                    <br /> Preferred Equity
                  </span>
                }
                explanation={
                  <Doc path="@/docs/pages/model/roi/cap-table-preferred-equity.mdx" />
                }
              />
            </Table.Th>
            <Table.Th>
              <LabelExplainer
                label={
                  <span style={{ whiteSpace: "nowrap", lineHeight: 1.6 }}>
                    Post Step-Up:
                    <br /> Common Equity
                  </span>
                }
                explanation={
                  <Doc path="@/docs/pages/model/roi/cap-table-common-equity.mdx" />
                }
              />
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody style={{ whiteSpace: "nowrap" }}>
          {values
            .filter((row) => row.isVisible)
            .map((row, i) => (
              <Table.Tr
                key={i}
                ff={monospaceFontFamily}
                ta={"right"}
                {...(row?.highlight ? { fw: 700, bg: "infomatic.0" } : {})}
              >
                <FlashOnChangeTD content={row.preferred?.toString()}>
                  {row.preferred !== null
                    ? formatMoney(Number(row.preferred))
                    : ""}
                </FlashOnChangeTD>

                <FlashOnChangeTD content={row.common?.toString()}>
                  {row.common !== null ? formatMoney(Number(row.common)) : ""}
                </FlashOnChangeTD>
              </Table.Tr>
            ))}
        </Table.Tbody>
      </Table>
    </Panel>
  );
};
